import { resumeCustomerIntent } from '../services/customerIntentService';
import CustomerOrderPreview from '../components/CustomerOrderPreview';
import { isCompletedOrder } from '../utils/orderPresentation';
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api';
import CustomerCallLogin from '../components/CustomerCallLogin';
import CustomerPaymentMethods from '../components/CustomerPaymentMethods';
import { useCart } from '../context/CartContext';
import { useFavorites } from '../context/FavoriteContext';
import ProductCard from '../components/ProductCard';
import { getImageUrl } from '../utils/image';
import {
  logout as logoutCustomer,
  restoreCustomer,
  saveCustomer,
  updateProfile,
} from '../services/customerService';

function ProfilePageDesktop() {
  const location = useLocation();
  const navigate = useNavigate();

  const [step, setStep] = useState('phone');
  const [profile, setProfile] = useState(null);
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('data');
  const [changingPassword, setChangingPassword] = useState(false);
  const { dispatch: cartDispatch, items: cartItems } = useCart();
  const { dispatch: favDispatch, items: favItems } = useFavorites();

  const [activeOrders, setActiveOrders] = useState([]);
  const [completedOrders, setCompletedOrders] = useState([]);

  const loadOrders = useCallback(async () => {
    try {
      const res = await api.get('/api/orders/customer');
      const allOrders = res.data;
      setActiveOrders(allOrders.filter(o => !isCompletedOrder(o)));
      setCompletedOrders(allOrders.filter(isCompletedOrder));
    } catch (err) {
      console.error(err);
      setActiveOrders([]);
      setCompletedOrders([]);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const restoreProfile = async () => {
      try {
        const session = await restoreCustomer();
        if (!session || cancelled) return;

        const { customer, cart, favorites } = session;
        setProfile(customer);
        setName(customer.name || '');
        setStep('profile');

        if (cart) {
          const cartData = JSON.parse(cart);
          cartDispatch({ type: 'LOAD_CART', payload: cartData });
        }
        if (favorites) {
          const favData = JSON.parse(favorites);
          favDispatch({ type: 'LOAD_FAVORITES', payload: favData });
        }
        await loadOrders();
      } catch (err) {
        if (!cancelled && err.response?.status !== 401 && err.response?.status !== 403) {
          console.error('Ошибка восстановления профиля', err);
        }
      }
    };

    restoreProfile();
    return () => { cancelled = true; };
  }, [cartDispatch, favDispatch, loadOrders]);


  const handleAuthenticated = async (session) => {
    const next = await resumeCustomerIntent(session);
    const userProfile = session.customer;
    cartDispatch({ type: 'LOAD_CART', payload: next.cart, ownerPhone: userProfile.phone });
    favDispatch({ type: 'LOAD_FAVORITES', payload: next.favorites, ownerPhone: userProfile.phone });
    setProfile(userProfile); setName(userProfile.name || ''); setStep('profile'); setChangingPassword(false); setMessage(''); setError(''); loadOrders();
    const to = next.to || location.state?.from;
    if (to && to.startsWith('/') && !to.startsWith('//')) navigate(to, { replace: true, state: { notice: next.notice } });
  };

  const handleSaveName = async () => {
    if (!profile) return;
    try {
      await updateProfile({ name: name.trim() });
      const updatedProfile = { ...profile, name: name.trim() };
      saveCustomer(updatedProfile);
      setProfile(updatedProfile);
      setMessage('Имя сохранено');
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка сохранения имени');
    }
  };

  const handleLogout = async () => {
    await logoutCustomer(profile?.phone, cartItems, favItems);
    setProfile(null);

    setName('');
    setStep('phone');
    setMessage('');
    setError('');
    cartDispatch({ type: 'CLEAR_CART' });
    favDispatch({ type: 'CLEAR_FAVORITES' });
  };

  const renderOrder = (order) => <CustomerOrderPreview key={order.id} order={order} />;

  if (!profile || step !== 'profile') {
    return <><p className="buyer-login-reason" role="status">{location.state?.loginReason}</p><CustomerCallLogin onAuthenticated={handleAuthenticated} /></>;
  }
  if (changingPassword) return <div>
    <button type="button" className="customer-login-link" onClick={() => setChangingPassword(false)}>Вернуться в профиль</button>
    <CustomerCallLogin initialMode="reset" onAuthenticated={handleAuthenticated} />
  </div>;

  return (
    <div className="profile-page-container">
      {/* Данные профиля */}
      <div className="profile-data-block">
        <p><strong>Телефон:</strong> {profile.phone}</p>
        {name && <p><strong>Имя:</strong> {name}</p>}
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 14, fontWeight: 500, color: '#1e293b', marginBottom: 6, display: 'block' }}>Ваше имя</label>
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Введите имя"
          className="profile-input"
          style={{ marginBottom: 12 }}
        />
        <button onClick={handleSaveName} className="profile-save-btn">
          Сохранить имя
        </button>
      </div>

      {/* Вертикальные вкладки */}
      <div className="profile-tabs-vertical">
        {['data', 'orders', 'purchases', 'favorites', 'reviews'].map(tab => (
          <button
            key={tab}
            className={`profile-tab-vertical ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            <span>
              {tab === 'data' ? 'Мои данные' :
               tab === 'orders' ? `Заказы (${activeOrders.length})` :
               tab === 'purchases' ? 'Покупки' :
               tab === 'favorites' ? `Избранное (${favItems.length})` : 'Отзывы и вопросы'}
            </span>
            <span>›</span>
          </button>
        ))}
      </div>

      {/* Содержимое вкладки */}
      <div className="profile-tab-content">
        {activeTab === 'data' && (
          <div>
            <CustomerPaymentMethods />
            <button type="button" className="profile-btn profile-btn--outline" onClick={() => setChangingPassword(true)}>Создать или сменить пароль</button>
            <button onClick={handleLogout} className="profile-logout-btn">
              Выйти
            </button>
            {message && <p className="profile-message">{message}</p>}
          </div>
        )}

        {activeTab === 'orders' && (
          <div>
            <h3 style={{ marginBottom: 16 }}>Активные заказы</h3>
            <a href="/orders" className="order-secondary-button" style={{ marginBottom: 16 }}>Открыть полный список заказов</a>
            {activeOrders.length === 0 ? <p style={{ color: '#94a3b8' }}>Нет активных заказов</p> : activeOrders.map(renderOrder)}
          </div>
        )}

        {activeTab === 'purchases' && (
          <div>
            <h3 style={{ marginBottom: 16 }}>История покупок</h3>
            {completedOrders.length === 0 ? <p style={{ color: '#94a3b8' }}>Нет завершённых заказов</p> : completedOrders.map(renderOrder)}
          </div>
        )}

        {activeTab === 'favorites' && (
          <div>
            {favItems.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#94a3b8', padding: '40px 0' }}>Нет избранных товаров</p>
            ) : (
              <div className="products-grid">
                {favItems.map(product => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'reviews' && (
          <div style={{ textAlign: 'center', padding: '30px 0', color: '#94a3b8' }}>
            Здесь будут ваши отзывы и вопросы (в разработке)
          </div>
        )}
      </div>
    </div>
  );
}

export default ProfilePageDesktop;
